import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Phone, Mail, MapPin, RefreshCw, Search, Users, AlertCircle } from "lucide-react";
import { fetchCustomerAgentListNetwork, fetchCustomerAgentListContact } from "./networkApi";
import "./network.css";
import "./customerAgents.css";

const SEARCH_THRESHOLD = 4;
const ASSET_URL = process.env.REACT_APP_ASSET_URL || "";

const clean = (value) => {
  if (value === undefined || value === null) return "";
  const text = String(value).trim();
  return text && text !== "null" && text !== "undefined" ? text : "";
};

// Both customer endpoints return the partner (User) who added the logged-in customer.
// Only the fields below are read; nothing else from the payload is displayed.
const toAgent = (user) => {
  const firstName = clean(user?.first_name);
  const lastName = clean(user?.last_name);
  const photo = clean(user?.photo);
  const userType = clean(user?.userType?.name ?? user?.userType).toUpperCase();
  return {
    key: String(user?.id ?? clean(user?.email_address).toLowerCase()),
    name: [firstName, lastName].filter(Boolean).join(" ") || "Partner",
    role: userType.includes("AGENT") ? "Agent" : "Partner",
    phone: clean(user?.mobile_number),
    email: clean(user?.email_address),
    city: clean(user?.city),
    state: clean(user?.state),
    photo: photo ? (/^https?:/i.test(photo) ? photo : `${ASSET_URL}${photo}`) : "",
  };
};

const extractUsers = (result) => {
  if (result.status !== "fulfilled") return null;
  const users = result.value?.users;
  // A "no agents" reply comes back as a non-SUCCESS status without users: treat as empty
  return Array.isArray(users) ? users : [];
};

const CustomerAgentsPage = () => {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [failedPhotos, setFailedPhotos] = useState({});

  const loadAgents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const results = await Promise.allSettled([
      fetchCustomerAgentListNetwork(),
      fetchCustomerAgentListContact(),
    ]);
    const lists = results.map(extractUsers);

    if (lists.every((list) => list === null)) {
      setAgents([]);
      setError("Unable to load your agents. Please check your connection and try again.");
    } else {
      // A partner can have added the customer to both the network and the contacts: show once
      const byKey = new Map();
      lists.flat().forEach((user) => {
        if (!user) return;
        const agent = toAgent(user);
        if (!byKey.has(agent.key)) byKey.set(agent.key, agent);
      });
      setAgents(Array.from(byKey.values()));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const visibleAgents = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => `${a.name} ${a.city} ${a.state}`.toLowerCase().includes(q));
  }, [agents, query]);

  return (
    <div className="net-hub cag">
      <div className="net-header">
        <div className="net-header__glow" />
        <div className="net-header__content">
          <div className="net-header__info">
            <h1>My Agents</h1>
            <span className="net-header__subtext">Partners who added you to their network</span>
          </div>
          <button type="button" className="net-btn net-btn--secondary" onClick={loadAgents} disabled={isLoading}>
            <RefreshCw size={14} className={isLoading ? "net-spin" : ""} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {!isLoading && !error && agents.length >= SEARCH_THRESHOLD && (
        <div className="net-search-input-wrap cag__search">
          <Search size={16} className="net-search-icon" />
          <input
            type="text"
            className="net-search-input"
            placeholder="Search agents by name or city..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}

      {isLoading ? (
        <div className="net-grid" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="net-card cag__skeleton" />
          ))}
        </div>
      ) : error ? (
        <div className="net-empty">
          <div className="net-empty__icon-wrap">
            <AlertCircle size={30} />
          </div>
          <h3 className="net-empty__title">Something went wrong</h3>
          <p className="net-empty__desc">{error}</p>
          <button type="button" className="net-btn net-btn--primary" onClick={loadAgents}>
            Retry
          </button>
        </div>
      ) : agents.length === 0 ? (
        <div className="net-empty">
          <div className="net-empty__icon-wrap">
            <Users size={30} />
          </div>
          <h3 className="net-empty__title">No agents connected yet</h3>
          <p className="net-empty__desc">
            No agents connected yet. When a partner adds you to their network, they will appear here.
          </p>
        </div>
      ) : visibleAgents.length === 0 ? (
        <div className="net-empty">
          <h3 className="net-empty__title">No matching agents</h3>
          <p className="net-empty__desc">Try a different name or city.</p>
        </div>
      ) : (
        <div className="net-grid">
          {visibleAgents.map((agent) => {
            const location = [agent.city, agent.state].filter(Boolean).join(", ");
            const showPhoto = agent.photo && !failedPhotos[agent.key];
            return (
              <div key={agent.key} className="net-card">
                <div className="net-card__header">
                  <div className="net-card__profile">
                    <div className="net-card__avatar">
                      {showPhoto ? (
                        <img
                          src={agent.photo}
                          alt=""
                          className="cag__photo"
                          onError={() => setFailedPhotos((prev) => ({ ...prev, [agent.key]: true }))}
                        />
                      ) : (
                        agent.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="net-card__name-col">
                      <h3 className="net-card__name">{agent.name}</h3>
                      <span className="net-card__type-tag">{agent.role}</span>
                    </div>
                  </div>
                </div>
                <div className="net-card__details">
                  {location && (
                    <div className="net-card__detail-row">
                      <MapPin size={14} className="net-card__detail-icon" />
                      <span>{location}</span>
                    </div>
                  )}
                  {agent.phone && (
                    <div className="net-card__detail-row">
                      <Phone size={14} className="net-card__detail-icon" />
                      <a href={`tel:${agent.phone}`}>{agent.phone}</a>
                    </div>
                  )}
                  {agent.email && (
                    <div className="net-card__detail-row">
                      <Mail size={14} className="net-card__detail-icon" />
                      <a href={`mailto:${agent.email}`}>{agent.email}</a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerAgentsPage;
